import React, { useRef } from 'react';
import landownerBgWebp from '../assets/landowner-bg.webp';
import TypingAnimation from './TypingAnimation';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useRAFScroll } from '../hooks/useRAFScroll';
import { mobileQuery } from '../utils/mediaQueries';
import './LandownerPartnerships.css';

const PARALLAX_SPEEDS = { eyebrow: 0.10, heading: 0.14, body: 0.18, benefits: 0.22 } as const;
const PARALLAX_SPEEDS_MOBILE = { eyebrow: 0.07, heading: 0.10, body: 0.13, benefits: 0.16 } as const;

const BENEFITS: readonly string[] = [
  'Upfront and staged income',
  'Retain ownership during option period',
  'No capital expenditure required',
  'Defined pathway to project delivery',
];

export default function LandownerPartnerships(): React.JSX.Element {
  const bgRef = useRef<HTMLDivElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const benefitsWrapRef = useRef<HTMLDivElement>(null);

  const sectionRef = useIntersectionObserver<HTMLElement>(
    (entry) => entry.target.classList.add('landowner--zooming'),
    { threshold: 0.1 },
  );

  const benefitsRef = useIntersectionObserver<HTMLDivElement>(
    (entry) => entry.target.classList.add('landowner__benefits--visible'),
    { threshold: 0.2 },
  );

  useRAFScroll(() => {
    const el = sectionRef.current;
    if (!el) return;
    const { top } = el.getBoundingClientRect();
    const delta = window.innerHeight / 2 - (top + el.offsetHeight / 2);
    const speeds = mobileQuery.matches ? PARALLAX_SPEEDS_MOBILE : PARALLAX_SPEEDS;

    if (bgRef.current) bgRef.current.style.transform = `translateY(${delta * 0.4}px)`;
    if (eyebrowRef.current) eyebrowRef.current.style.transform = `translateY(${delta * -speeds.eyebrow}px)`;
    if (headingRef.current) headingRef.current.style.transform = `translateY(${delta * -speeds.heading}px)`;
    if (bodyRef.current) bodyRef.current.style.transform = `translateY(${delta * -speeds.body}px)`;
    if (benefitsWrapRef.current)
      benefitsWrapRef.current.style.transform = `translateY(${delta * -speeds.benefits}px)`;
  });

  return (
    <section
      id="for-landowners"
      className="landowner"
      ref={sectionRef}
      aria-labelledby="landowner-heading"
    >
      <div ref={bgRef} className="landowner__bg" aria-hidden="true">
        <img
          src={landownerBgWebp}
          alt=""
          className="landowner__bg-img"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
        />
      </div>
      <div className="landowner__overlay" aria-hidden="true" />
      <div className="landowner__content">
        <p className="landowner__eyebrow" ref={eyebrowRef}>
          <TypingAnimation startOnView duration={100}>
            Your land. Long-term value.
          </TypingAnimation>
        </p>
        <h2 id="landowner-heading" className="landowner__heading" ref={headingRef}>
          Landowner Partnerships
        </h2>
        <div className="landowner__body" ref={bodyRef}>
          <p>
            Land is central to the energy transition. Everfield Group works directly with Australian
            landholders to unlock strategic sites through structured option-to-purchase
            agreements with clear outcomes.
          </p>
          <p>
            Our option structures provide landowners with upfront and staged payments while
            maintaining ownership during the option period. This creates a clear pathway for
            development without requiring immediate sale or operational involvement.
          </p>
        </div>
        <div ref={benefitsWrapRef}>
          <p className="landowner__benefits-label">Benefits:</p>
          <div
            className="landowner__benefits"
            ref={benefitsRef}
            role="list"
            aria-label="Partnership benefits"
          >
            {BENEFITS.map((benefit, i) => (
              <React.Fragment key={benefit}>
                {i > 0 && <div className="landowner__divider" aria-hidden="true" />}
                <div className="landowner__benefit" role="listitem">
                  {benefit}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
